import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { background, border, color, flexbox, grid, layout, position, space, typography, variant } from 'styled-system'

import Box from '../Box'
import LoadingBubbles from '../LoadingBubbles'

const StyledButton = styled.button`
  display: inline-flex;
  border-radius: 5px;
  border-style: solid;
  border-width: 1px;
  width: 100%;
  flex-direction: row;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  position: relative;
  text-align: center;
  text-decoration: none;
  letter-spacing: 1px;
  line-height: 1.5;
  cursor: pointer;
  ${background}
  ${border}
  ${color}
  ${flexbox}
  ${grid}
  ${layout}
  ${space}
  ${position}
  ${typography}
  ${variant({
    prop: 'size',
    scale: 'buttonSizes',
    variants: {
      tiny: {
        fontSize: 10,
        padding: '3px 8px',
      },
      small: {
        fontSize: 12,
        padding: '5px 10px',
      },
      medium: {
        fontSize: 13,
        padding: '8px 15px',
      },
      large: {
        fontSize: 14,
        padding: '11.5px 20px',
      },
    },
  })}
  ${variant({
    prop: 'buttonStyle',
    scale: 'buttons',
    variants: {
      disabled: {
        color: 'lightGrey',
        backgroundColor: 'whiteGrey',
        borderColor: 'lightestGrey',
      },
      primaryCreate: {
        color: 'white',
        backgroundColor: 'green',
        borderColor: 'green',
        '&:hover': {
          backgroundColor: '#3C9B5F',
        },
      },
      primaryEdit: {
        color: 'white',
        backgroundColor: 'blue',
        borderColor: 'blue',
        '&:hover': {
          backgroundColor: '#02A5CB',
        },
      },
      primaryDestroy: {
        color: 'white',
        backgroundColor: 'red',
        borderColor: 'red',
        '&:hover': {
          backgroundColor: '#CB0017',
        },
      },
      primaryUtility: {
        color: 'white',
        backgroundColor: 'darkGrey',
        borderColor: 'darkGrey',
        '&:hover': {
          backgroundColor: '#2b2b2b',
        },
      },
      ghostCreate: {
        color: 'green',
        backgroundColor: 'transparent',
        borderColor: 'green',
        '&:hover': {
          color: 'white',
          backgroundColor: 'green',
          borderColor: 'green',
        },
      },
      ghostEdit: {
        color: 'blue',
        backgroundColor: 'transparent',
        borderColor: 'blue',
        '&:hover': {
          color: 'white',
          backgroundColor: 'blue',
        },
      },
      ghostDestroy: {
        color: 'red',
        backgroundColor: 'transparent',
        borderColor: 'red',
        '&:hover': {
          color: 'white',
          backgroundColor: 'red',
        },
      },
      ghostUtility: {
        color: 'darkGrey',
        backgroundColor: 'transparent',
        borderColor: 'darkGrey',
        '&:hover': {
          color: 'white',
          backgroundColor: 'darkGrey',
        },
      },
      secondary: {
        color: 'bodyFontColor',
        backgroundColor: 'transparent',
        borderColor: 'lightestGrey',
        '&:hover': {
          color: 'bodyFontColor',
          backgroundColor: 'lightestGrey',
          borderColor: 'lightestGrey',
        },
      },
      secondaryCreate: {
        color: 'green',
        backgroundColor: 'transparent',
        borderColor: 'lightestGrey',
        '&:hover': {
          color: 'white',
          backgroundColor: 'green',
          borderColor: 'green',
        },
      },
      secondaryEdit: {
        color: 'blue',
        backgroundColor: 'transparent',
        borderColor: 'lightestGrey',
        '&:hover': {
          color: 'white',
          backgroundColor: 'blue',
          borderColor: 'blue',
        },
      },
      secondaryDestroy: {
        color: 'red',
        backgroundColor: 'transparent',
        borderColor: 'lightestGrey',
        '&:hover': {
          color: 'white',
          backgroundColor: 'red',
          borderColor: 'red',
        },
      },
      secondaryUtility: {
        color: 'darkGrey',
        backgroundColor: 'transparent',
        borderColor: 'lightestGrey',
        '&:hover': {
          color: 'white',
          backgroundColor: 'darkGrey',
          borderColor: 'darkGrey',
        },
      },
    },
  })}
`

const Button = (props) => {
  const {
    buttonStyle,
    children,
    disabled,
    icon,
    iconMargin,
    iconPosition,
    loading,
    loadingBubbleColor,
    ...restProps
  } = props

  if (disabled){
    return (
      <StyledButton buttonStyle="disabled" disabled {...restProps}>
        {children}
      </StyledButton>
    )
  }

  if (loading){
    return (
      <StyledButton buttonStyle={buttonStyle} disabled {...restProps}>
        <LoadingBubbles color={loadingBubbleColor || "rgba(0, 0, 0, 0.15)"} />
      </StyledButton>
    )
  }

  return (
    <StyledButton
      buttonStyle={buttonStyle}
      flexDirection={iconPosition === 'right'? "row-reverse" : 'row'}
      {...restProps}
    >
      {icon && (
        <Box as="span"
          marginLeft={iconPosition === 'right' && children ? iconMargin : 0}
          marginRight={iconPosition === 'left' && children ? iconMargin : 0}
          width="auto">
          {icon}
        </Box>
      )}
      {children}
    </StyledButton>
  )
}

Button.propTypes = {
  buttonStyle: PropTypes.oneOf([
    'disabled',
    'primaryCreate',
    'primaryEdit',
    'primaryDestroy',
    'primaryUtility',
    'ghostCreate',
    'ghostEdit',
    'ghostDestroy',
    'ghostUtility',
    'secondary',
    'secondaryCreate',
    'secondaryEdit',
    'secondaryDestroy',
    'secondaryUtility',
  ]),
  disabled: PropTypes.bool,
  icon: PropTypes.node,
  iconMargin: PropTypes.string,
  iconPosition: PropTypes.oneOf(['left', 'right']),
  loading: PropTypes.bool,
  size: PropTypes.oneOfType([PropTypes.array,  PropTypes.oneOf(['tiny', 'small', 'medium', 'large'])]),
}

Button.defaultProps = {
  iconMargin: 'small',
  iconPosition: 'left'
}

export default Button
